// Small accessible form controls for the setup screen: a switch, a
// segmented radio group (arrow keys move and select, one tab stop) and a
// number stepper. Raw <button>s carry `cursor-pointer` explicitly —
// Tailwind v4 no longer sets it (ui-invariants checks every button).

import { cn } from '@aod/ui/lib/utils';
import { Minus, Plus } from 'lucide-react';
import {
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useId,
  useRef,
} from 'react';

/**
 * Arrow/Home/End handling for a group of radios with a roving tab stop.
 * Returns the index to select, or null when the key isn't one we handle.
 */
export function nextRovingIndex(key: string, index: number, count: number): number | null {
  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return (index + 1) % count;
    case 'ArrowLeft':
    case 'ArrowUp':
      return (index - 1 + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/** Keydown handler for a radiogroup whose radios are its direct `[role=radio]` descendants. */
export function useRovingRadio<T>(values: readonly T[], value: T, onChange: (v: T) => void) {
  const ref = useRef<HTMLDivElement>(null);
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const current = Math.max(0, values.indexOf(value));
    const next = nextRovingIndex(e.key, current, values.length);
    if (next === null) return;
    e.preventDefault();
    const v = values[next];
    if (v === undefined) return;
    onChange(v);
    const radios = ref.current?.querySelectorAll<HTMLElement>('[role="radio"]');
    radios?.[next]?.focus();
  };
  return { ref, onKeyDown };
}

interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'role' | 'type'> {
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  readonly label: string;
  readonly describedBy?: string;
}

/** role=switch button; extra props (data-testid, appDom attributes) land on the button. */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  describedBy,
  title,
  className,
  ...rest
}: SwitchProps) {
  return (
    <button
      {...rest}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-describedby={describedBy}
      title={title}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border border-transparent transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-40',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'inline-block size-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-[21px]' : 'translate-x-[1px]',
        )}
      />
    </button>
  );
}

export interface SegmentOption<T> {
  readonly value: T;
  readonly label: string;
}

interface SegmentedProps<T> {
  readonly label: string;
  readonly description?: string;
  readonly value: T;
  readonly options: readonly SegmentOption<T>[];
  readonly onChange: (v: T) => void;
  readonly disabled?: boolean;
  readonly note?: ReactNode;
}

/** A labelled row with a segmented radio group; stacks under the label on phones. */
export function SegmentedRow<T extends string | number>({
  label,
  description,
  value,
  options,
  onChange,
  disabled,
  note,
}: SegmentedProps<T>) {
  const id = useId();
  const values = options.map((o) => o.value);
  const { ref, onKeyDown } = useRovingRadio(values, value, onChange);
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <div id={`${id}-label`} className="text-sm font-medium text-foreground">
          {label}
        </div>
        {description ? (
          <p id={`${id}-desc`} className="text-xs text-muted-foreground">
            {description}
          </p>
        ) : null}
        {note}
      </div>
      <div
        ref={ref}
        role="radiogroup"
        aria-labelledby={`${id}-label`}
        aria-describedby={description ? `${id}-desc` : undefined}
        aria-disabled={disabled || undefined}
        onKeyDown={disabled ? undefined : onKeyDown}
        className={cn(
          'flex w-full shrink-0 overflow-hidden rounded-lg border border-border bg-muted/50 p-0.5 sm:w-auto',
          disabled && 'opacity-50',
        )}
      >
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <button
              key={String(opt.value)}
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: segmented control styled as buttons; radio semantics via role + aria-checked
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              className={cn(
                'min-h-9 flex-1 cursor-pointer whitespace-nowrap rounded-md px-2.5 text-xs font-medium transition-colors sm:flex-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'disabled:cursor-not-allowed',
                selected
                  ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface SwitchRowProps {
  readonly label: string;
  readonly description?: string;
  readonly checked: boolean;
  readonly onChange: (v: boolean) => void;
}

export function SwitchRow({ label, description, checked, onChange }: SwitchRowProps) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{label}</div>
        {description ? (
          <p id={`${id}-desc`} className="text-xs text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      <Switch
        checked={checked}
        onChange={onChange}
        label={label}
        describedBy={description ? `${id}-desc` : undefined}
      />
    </div>
  );
}

interface StepperRowProps {
  readonly label: string;
  readonly description?: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly format: (v: number) => string;
  readonly onChange: (v: number) => void;
  readonly decreaseLabel: string;
  readonly increaseLabel: string;
  readonly disabled?: boolean;
  readonly note?: ReactNode;
}

/** − value + with a live value readout; the value is an `output` for screen readers. */
export function StepperRow({
  label,
  description,
  value,
  min,
  max,
  step,
  format,
  onChange,
  decreaseLabel,
  increaseLabel,
  disabled,
  note,
}: StepperRowProps) {
  const id = useId();
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v / step) * step));
  const btn =
    'inline-flex size-9 cursor-pointer items-center justify-center rounded-md text-foreground transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div id={`${id}-label`} className="text-sm font-medium text-foreground">
          {label}
        </div>
        {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
        {note}
      </div>
      <fieldset
        aria-labelledby={`${id}-label`}
        disabled={disabled}
        className={cn(
          'm-0 flex shrink-0 items-center gap-0.5 rounded-lg border border-border bg-muted/50 p-0.5',
          disabled && 'opacity-50',
        )}
      >
        <button
          type="button"
          aria-label={decreaseLabel}
          className={btn}
          disabled={disabled || value <= min}
          onClick={() => onChange(clamp(value - step))}
        >
          <Minus className="size-4" aria-hidden />
        </button>
        <output
          aria-live="polite"
          className="min-w-14 text-center text-sm font-semibold tabular-nums text-foreground"
        >
          {format(value)}
        </output>
        <button
          type="button"
          aria-label={increaseLabel}
          className={btn}
          disabled={disabled || value >= max}
          onClick={() => onChange(clamp(value + step))}
        >
          <Plus className="size-4" aria-hidden />
        </button>
      </fieldset>
    </div>
  );
}
